import { WidgetId } from 'types/idTypes';
import { Widget, WidgetType } from 'types/widget.types';
import { calculateHeightByRatio } from 'utils/calculateAspectRatioFit';
import { adjustSVGViewBox, getGridIconSize } from 'widgets/IconWidget/IconWidget.helpers';
import { IconWidgetData, IconWidgetType } from 'widgets/IconWidget/IconWidget.types';
import { ImageWidgetData } from 'widgets/ImageWidget/ImageWidget.types';
import { getWidgetTypeFromId } from 'widgets/Widget.helpers';
import { AllWidgetData, NewWidget } from 'widgets/Widget.types';

type HandleReplaceData = {
  newWidgetData: AllWidgetData | NewWidget;
  widgetData: Widget;
  viewBox: string;
  widgetId: WidgetId;
};

/**
 * Function to handle the replace data based on the widget type
 * @param widgetId
 * @param widgetData widget data of the selected widget
 * @param newWidgetData widget data of the new replace widget
 * @param viewBox - The current widget metadata object
 *
 * @returns the data that needs to be replaced
 */
export const replaceDataSameWidgetType = ({ widgetId, widgetData, newWidgetData, viewBox }: HandleReplaceData) => {
  const widgetType = getWidgetTypeFromId(widgetId);

  switch (widgetType) {
    // update the icon widget with a new icon
    case WidgetType.Icon: {
      const { iconId: newIconId, iconMetadata: newIconMetadata } = newWidgetData as IconWidgetData;
      const { type = IconWidgetType.Single, widthPx, heightPx } = widgetData as IconWidgetData;

      if (type === IconWidgetType.Grid) {
        const { gridItemWidthPx, gridItemHeightPx } = widgetData as IconWidgetData;
        const maxSize = Math.max(gridItemWidthPx!, gridItemHeightPx!);

        const { width: newGridWidthPx, height: newGridHeightPx } = adjustSVGViewBox({ viewBox, maxSize });
        const { height: newHeightPx, width: newWidthPx } = getGridIconSize(widgetId, newGridWidthPx, newGridHeightPx);

        return {
          heightPx: newHeightPx,
          widthPx: newWidthPx,
          gridItemWidthPx: newGridWidthPx,
          gridItemHeightPx: newGridHeightPx,
          iconId: newIconId,
          iconMetadata: newIconMetadata,
        };
      }

      const maxSize = Math.max(widthPx, heightPx);
      const { width: newWidthPx, height: newHeightPx } = adjustSVGViewBox({ viewBox, maxSize });

      // TODO update `iconMetadata` when icons8 is included in replace menu
      return { iconId: newIconId, heightPx: newHeightPx, widthPx: newWidthPx, iconMetadata: newIconMetadata };
    }

    case WidgetType.Image: {
      const { widthPx: originalWidth, heightPx: originalHeight } = widgetData as ImageWidgetData;
      const { url } = newWidgetData as ImageWidgetData;

      const imageMeta = convertImageSizeMeta(originalWidth, originalHeight, newWidgetData as ImageWidgetData);

      return {
        url,
        widthPx: originalWidth,
        heightPx: originalHeight,
        ...imageMeta,
      };
    }

    default:
      return {};
  }
};

/**
 * A helper function to updated the new widget based on the widget that will be replaced.
 * This would update the newWidget object instead of returning a new object.
 *
 * @param newWidget the new widget
 * @param existingWidget the existing widget data from the widget that will be replaced.
 */
export const replaceDataDifferentWidgetType = (
  newWidget: NewWidget,
  existingWidget: Widget | AllWidgetData,
): NewWidget => {
  switch (newWidget.widgetType) {
    case WidgetType.Icon: {
      const { widgetData } = newWidget;
      const { width: widthPx, height: heightPx } = calculateHeightByRatio(
        widgetData.widthPx,
        widgetData.heightPx,
        existingWidget.widthPx,
      );

      return {
        ...newWidget,
        widgetData: {
          ...newWidget.widgetData,
          leftPx: existingWidget.leftPx,
          topPx: existingWidget.topPx,
          rotateDeg: existingWidget.rotateDeg,
          widthPx,
          heightPx,
        } as IconWidgetData,
      };
    }
    case WidgetType.Image: {
      const { widgetData } = newWidget;
      const { width: widthPx, height: heightPx } = calculateHeightByRatio(
        widgetData.widthPx,
        widgetData.heightPx,
        existingWidget.widthPx,
      );

      return {
        ...newWidget,
        widgetData: {
          ...newWidget.widgetData,
          leftPx: existingWidget.leftPx,
          topPx: existingWidget.topPx,
          rotateDeg: existingWidget.rotateDeg,
          widthPx,
          heightPx,
          imageRect: {
            widthPx,
            heightPx,
          },
        } as ImageWidgetData,
      };
    }
    default:
      return {
        ...newWidget,
        widgetData: {
          ...newWidget.widgetData,
          leftPx: existingWidget.leftPx,
          topPx: existingWidget.topPx,
          rotateDeg: existingWidget.rotateDeg,
        },
      };
  }
};

/**
 * Check if the widget has a replace option
 * @param widgetId
 */
export const widgetHasReplace = (widgetId: WidgetId) => {
  if (!widgetId) return false;

  const widgetType = getWidgetTypeFromId(widgetId);
  if (widgetType === WidgetType.Icon) return true;
  if (widgetType === WidgetType.Image) return true;

  return false;
};

/**
 * Calculate the expected image meta such as crop and the imageRect
 * so any images can be rendered in a image widget with size (expectedWidth, expectedHeight)
 *
 * @param expectedWidth
 * @param expectedHeight
 * @param imageMeta the current image meta, which need to be converted.
 * @returns the calculated image meta required on rendering a image in a expected size.
 */
export const convertImageSizeMeta = (
  expectedWidth: number,
  expectedHeight: number,
  imageMeta: Pick<ImageWidgetData, 'crop' | 'imageRect'>,
) => {
  const {
    crop,
    imageRect: { widthPx, heightPx },
  } = imageMeta;

  // If the size is the same as expected
  if (widthPx === expectedWidth && heightPx === expectedWidth) {
    return { crop, imageRect: { widthPx, heightPx } };
  }

  // Zoom the size to expected
  const ratio = Math.min(widthPx / expectedWidth, heightPx / expectedHeight);
  const calculatedRect = { widthPx: widthPx / ratio, heightPx: heightPx / ratio };

  // Calculate the cropping offset
  const yOffset = (calculatedRect.heightPx - expectedHeight) / 2;
  const xOffset = (calculatedRect.widthPx - expectedWidth) / 2;

  // Default the crop posiiton to the center
  const calculatedCrop = {
    top: yOffset,
    right: xOffset,
    bottom: yOffset,
    left: xOffset,
  };

  return { crop: calculatedCrop, imageRect: calculatedRect };
};

/**
 * Reset image size meta, set crop and image rect to fit whole image.
 *
 * @param widthPx initial width
 * @param heightPx initial height, by aspect ratio
 * @returns
 */
export const resetImageSizeMeta = (widthPx: number, heightPx: number): Pick<ImageWidgetData, 'crop' | 'imageRect'> => {
  return {
    crop: { top: 0, left: 0, right: 0, bottom: 0 },
    imageRect: {
      widthPx,
      heightPx,
    },
  };
};

/**
 * Check if the widget should include the photos tab in replace menu
 *
 * @param widgetType
 * @param type
 * @returns boolean
 */
export const shouldIncludePhotosTab = (widgetType: WidgetType | '', type: IconWidgetType | undefined) => {
  if (widgetType === WidgetType.Icon && type === IconWidgetType.Grid) return false;

  return true;
};
